/** ISO: 1 = Lunes … 7 = Domingo (coincide con EXTRACT(ISODOW) en PostgreSQL). */
export const DAY_OF_WEEK_NAMES_ES = [
  '',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
  'Domingo',
];

export function parseDayOfWeekNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseInt(String(value).trim(), 10);
  if (!Number.isFinite(n) || n < 1 || n > 7) return null;
  return n;
}

export function formatDayOfWeekLabel(value) {
  const n = parseDayOfWeekNumber(value);
  if (n == null) return null;
  return DAY_OF_WEEK_NAMES_ES[n];
}

export function isDayOfWeekKeySet(keys) {
  if (!keys?.length) return false;
  return keys.every((k) => parseDayOfWeekNumber(k) != null);
}

export function parseMonthOfYearNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseInt(String(value).trim(), 10);
  if (!Number.isFinite(n) || n < 1 || n > 12) return null;
  return n;
}

/** Indice 1 = Enero … 12 = Diciembre. */
export const MONTH_NAMES_ES = [
  '',
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

export function formatMonthOfYearLabel(value) {
  const n = parseMonthOfYearNumber(value);
  if (n == null) return null;
  return MONTH_NAMES_ES[n];
}

export function parseDayOfMonthNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseInt(String(value).trim(), 10);
  if (!Number.isFinite(n) || n < 1 || n > 31) return null;
  return n;
}

export function formatDayOfMonthLabel(value) {
  const n = parseDayOfMonthNumber(value);
  if (n == null) return null;
  return String(n);
}

export function isDayOfMonthKeySet(keys) {
  if (!keys?.length) return false;
  return keys.every((k) => parseDayOfMonthNumber(k) != null);
}

/** xDateBucket guardado en el chart o inferido del visualizationSpec (gráficos anteriores). */
export function resolveXDateBucket(chart) {
  if (chart?.xDateBucket) return chart.xDateBucket;
  const ep = chart?.visualizationSpec?.episodeAnalysis;
  if (ep?.enabled && ep.chartSource !== 'original' && ep.xDateBucket && ep.xDateBucket !== 'raw') {
    return ep.xDateBucket;
  }
  const bucket = chart?.visualizationSpec?.dimension?.dateBucket;
  if (bucket && bucket !== 'raw') return bucket;
  return undefined;
}

/** Formato de visualización guardado en el chart o inferido del spec. */
export function resolveXDateDisplayFormat(chart) {
  if (chart?.xDateDisplayFormat) return chart.xDateDisplayFormat;
  const ep = chart?.visualizationSpec?.episodeAnalysis;
  if (ep?.enabled && ep.chartSource !== 'original' && ep.xDateDisplayFormat && ep.xDateDisplayFormat !== 'auto') {
    return ep.xDateDisplayFormat;
  }
  if (ep?.enabled && ep.chartSource !== 'original') {
    const cols = chart?.visualizationSpec?.tableColumns || [];
    const timeCol = cols.find(
      (c) =>
        c?.column &&
        c.role !== 'value' &&
        c.dateBucket &&
        ['minute', 'hour', 'raw'].includes(c.dateBucket) &&
        c.dateDisplayFormat &&
        c.dateDisplayFormat !== 'auto'
    );
    if (timeCol?.dateDisplayFormat) return timeCol.dateDisplayFormat;
  }
  const fmt = chart?.visualizationSpec?.dimension?.dateDisplayFormat;
  if (fmt && fmt !== 'auto') return fmt;
  return undefined;
}

function isDerivedAnalysisChart(chart) {
  const ep = chart?.visualizationSpec?.episodeAnalysis;
  if (ep?.enabled && ep.chartSource !== 'original') return true;
  const sz = chart?.visualizationSpec?.spatialZoneAnalysis;
  if (sz?.enabled && sz.chartSource !== 'original') return true;
  return false;
}

/** labelDateBucket (Series/color) guardado en el chart o inferido del visualizationSpec. */
export function resolveLabelDateBucket(chart) {
  if (chart?.labelDateBucket) return chart.labelDateBucket;
  if (isDerivedAnalysisChart(chart)) return undefined;
  const bucket = chart?.visualizationSpec?.series?.dateBucket;
  if (bucket && bucket !== 'raw') return bucket;
  return undefined;
}

/** Formato de visualización de la Series guardado en el chart o inferido del spec. */
export function resolveLabelDateDisplayFormat(chart) {
  if (chart?.labelDateDisplayFormat) return chart.labelDateDisplayFormat;
  if (isDerivedAnalysisChart(chart)) return undefined;
  const fmt = chart?.visualizationSpec?.series?.dateDisplayFormat;
  if (fmt && fmt !== 'auto') return fmt;
  return undefined;
}

/** Opciones de formato de fecha/hora (solo visualización, no afecta la consulta). */
export const DATE_DISPLAY_FORMAT_OPTIONS = [
  { value: 'auto', label: 'Automático (según agrupación)' },
  { value: 'date', label: 'Fecha — YYYY-MM-DD' },
  { value: 'datetime', label: 'Fecha y hora — YYYY-MM-DD HH:mm' },
  { value: 'datetimeSec', label: 'Fecha y hora (seg) — YYYY-MM-DD HH:mm:ss' },
  { value: 'time', label: 'Hora — HH:mm' },
  { value: 'timeSec', label: 'Hora (seg) — HH:mm:ss' },
  { value: 'localeDate', label: 'Fecha local — 21/07/2026' },
  { value: 'localeDatetime', label: 'Fecha y hora local' },
  { value: 'custom', label: 'Personalizado (plantilla)' },
];

export const CUSTOM_DATE_FORMAT_PREFIX = 'custom:';

export const DEFAULT_CUSTOM_DATE_FORMAT_PATTERN = '(weekday) dd-MM-yyyy';

/** Plantillas rápidas para el selector de formato personalizado. */
export const DATE_FORMAT_PATTERN_PRESETS = [
  { label: '(weekday) dd-MM-yyyy', pattern: '(weekday) dd-MM-yyyy' },
  { label: 'w dd-MM-YY', pattern: 'w dd-MM-YY' },
  { label: 'www dd/MM/yyyy', pattern: 'www dd/MM/yyyy' },
  { label: 'dd/MM/yyyy', pattern: 'dd/MM/yyyy' },
  { label: 'weekday dd-MM-yyyy HH:mm', pattern: 'weekday dd-MM-yyyy HH:mm' },
  { label: 'yyyy-MM-dd', pattern: 'yyyy-MM-dd' },
  { label: 'MMMM yyyy', pattern: 'MMMM yyyy' },
  { label: 'MMM yyyy', pattern: 'MMM yyyy' },
];

/** Tokens disponibles en plantillas personalizadas. */
export const DATE_FORMAT_TOKEN_HELP =
  'Tokens: yyyy YY yy MMMM MMM MM M dd d HH H mm m ss s weekday www w weekdayShort weekdayN';

// MMMM/MMM van antes que MM/M en la alternancia del regex -- una alternancia
// evalua de izquierda a derecha y usa la primera que matchea en cada posicion (no la
// mas larga), asi que si MM fuera primero "MMMM" se partiria en dos tokens "MM"+"MM".
const DATE_FORMAT_TOKEN_REGEX =
  /yyyy|MMMM|MMM|weekdayShort|weekdayN|weekday|www|YY|yy|MM|M|dd|d|HH|H|mm|m|ss|s|w/g;

const DATE_FORMAT_DATE_TOKENS = new Set(['yyyy', 'yy', 'YY', 'MMMM', 'MMM', 'MM', 'M', 'dd', 'd']);

const DATE_FORMAT_WEEKDAY_TOKENS = new Set(['weekday', 'weekdayShort', 'weekdayN', 'w', 'www']);

/** Nombre del mes en texto para los tokens MMMM (completo) y MMM (abreviado mayus). */
function resolveMonthToken(token, monthNum) {
  const name = MONTH_NAMES_ES[monthNum];
  if (!name) return '';
  if (token === 'MMMM') return name;
  if (token === 'MMM') return name.slice(0, 3).toUpperCase();
  return '';
}

function formatYearTwoDigits(year) {
  return pad2(Number(year) % 100);
}

function resolveWeekdayToken(token, isoDow) {
  const name = DAY_OF_WEEK_NAMES_ES[isoDow];
  if (!name) return '';
  switch (token) {
    case 'weekday':
      return name;
    case 'weekdayShort':
    case 'www':
      return name.slice(0, 3);
    case 'w':
      return name.charAt(0);
    case 'weekdayN':
      return String(isoDow);
    default:
      return '';
  }
}

export function isCustomDateDisplayFormat(fmt) {
  return Boolean(fmt && String(fmt).startsWith(CUSTOM_DATE_FORMAT_PREFIX));
}

export function getCustomDateDisplayPattern(fmt) {
  if (!isCustomDateDisplayFormat(fmt)) return '';
  return String(fmt).slice(CUSTOM_DATE_FORMAT_PREFIX.length);
}

export function buildCustomDateDisplayFormat(pattern) {
  const p = String(pattern ?? '').trim();
  return p ? `${CUSTOM_DATE_FORMAT_PREFIX}${p}` : '';
}

export function normalizeDateDisplayFormatSelectValue(fmt) {
  if (!fmt || fmt === 'auto') return 'auto';
  if (isCustomDateDisplayFormat(fmt)) return 'custom';
  return fmt;
}

/** Tokens de fecha/hora en la plantilla que no existen en el valor agrupado. */
export function getCustomPatternBucketWarning(pattern, dateBucket) {
  if (!pattern?.trim() || !dateBucket || dateBucket === 'raw') return null;
  const tokens = pattern.match(DATE_FORMAT_TOKEN_REGEX) || [];
  const hasDateTokens = tokens.some((t) => DATE_FORMAT_DATE_TOKENS.has(t));
  const hasWeekdayTokens = tokens.some((t) => DATE_FORMAT_WEEKDAY_TOKENS.has(t));
  if (dateBucket === 'dayOfWeek' && hasDateTokens) {
    return 'Con agrupación «día de la semana» solo aplican tokens de día (weekday, www, w, weekdayShort, weekdayN).';
  }
  if (dateBucket === 'hourOfDay' && (hasDateTokens || hasWeekdayTokens)) {
    return 'Con agrupación «hora del día» solo aplican tokens de hora (HH, H).';
  }
  if (dateBucket === 'year' && tokens.some((t) => !['yyyy', 'yy', 'YY'].includes(t))) {
    return 'Con agrupación «año» solo tiene sentido el token yyyy, yy o YY.';
  }
  if (
    dateBucket === 'month' &&
    tokens.some((t) => !['yyyy', 'yy', 'YY', 'MMMM', 'MMM', 'MM', 'M'].includes(t))
  ) {
    return 'Con agrupación «mes» solo aplican tokens yyyy, yy, YY, MMMM, MMM, MM o M.';
  }
  if (
    dateBucket === 'monthOfYear' &&
    tokens.some((t) => !['MMMM', 'MMM', 'MM', 'M'].includes(t))
  ) {
    return 'Con agrupación «mes del año» solo aplican tokens MMMM, MMM, MM o M.';
  }
  if (dateBucket === 'dayOfMonth' && tokens.some((t) => !['dd', 'd'].includes(t))) {
    return 'Con agrupación «día del mes» solo aplican tokens dd o d.';
  }
  return null;
}

function getIsoDowFromUtcDate(date) {
  const d = date.getUTCDay();
  return d === 0 ? 7 : d;
}

function replaceDateFormatTokens(pattern, replacer) {
  return String(pattern).replace(DATE_FORMAT_TOKEN_REGEX, (token) => replacer(token) ?? '');
}

function zonedCalendar(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  const y = Number(get('year'));
  const mo = Number(get('month'));
  const d = Number(get('day'));
  return {
    y,
    mo,
    d,
    hh: Number(get('hour')),
    mm: Number(get('minute')),
    ss: Number(get('second')),
    isoDow: getIsoDowFromUtcDate(new Date(Date.UTC(y, mo - 1, d))),
  };
}

export function formatWithCustomPattern(value, pattern, { dateBucket, timeZone } = {}) {
  if (!pattern?.trim()) return null;

  const dowNum = parseDayOfWeekNumber(value);
  if (dateBucket === 'dayOfWeek' && dowNum != null) {
    return replaceDateFormatTokens(pattern, (token) => {
      if (DATE_FORMAT_WEEKDAY_TOKENS.has(token)) return resolveWeekdayToken(token, dowNum);
      if (DATE_FORMAT_DATE_TOKENS.has(token)) return '';
      return '';
    });
  }

  if (dateBucket === 'hourOfDay') {
    const n = typeof value === 'number' ? value : parseInt(String(value).trim(), 10);
    if (Number.isFinite(n) && n >= 0 && n <= 23) {
      return replaceDateFormatTokens(pattern, (token) => {
        if (token === 'HH') return pad2(n);
        if (token === 'H') return String(n);
        return '';
      });
    }
  }

  if (dateBucket === 'year') {
    const s = String(value).trim();
    const y = /^\d{4}$/.test(s) ? s : null;
    if (y) {
      return replaceDateFormatTokens(pattern, (token) => {
        if (token === 'yyyy') return y;
        if (token === 'yy' || token === 'YY') return formatYearTwoDigits(y);
        return '';
      });
    }
  }

  if (dateBucket === 'month') {
    const s = String(value).trim();
    const m = /^\d{4}-\d{2}$/.test(s) ? s : null;
    if (m) {
      const [y, mo] = m.split('-');
      return replaceDateFormatTokens(pattern, (token) => {
        if (token === 'yyyy') return y;
        if (token === 'yy' || token === 'YY') return formatYearTwoDigits(y);
        if (token === 'MMMM' || token === 'MMM') return resolveMonthToken(token, parseInt(mo, 10));
        if (token === 'MM') return mo;
        if (token === 'M') return String(parseInt(mo, 10));
        return '';
      });
    }
  }

  if (dateBucket === 'monthOfYear') {
    const n = parseMonthOfYearNumber(value);
    if (n != null) {
      return replaceDateFormatTokens(pattern, (token) => {
        if (token === 'MMMM' || token === 'MMM') return resolveMonthToken(token, n);
        if (token === 'MM') return pad2(n);
        if (token === 'M') return String(n);
        return '';
      });
    }
  }

  if (dateBucket === 'dayOfMonth') {
    const n = parseDayOfMonthNumber(value);
    if (n != null) {
      return replaceDateFormatTokens(pattern, (token) => {
        if (token === 'dd') return pad2(n);
        if (token === 'd') return String(n);
        return '';
      });
    }
  }

  const parsed = parseTemporalValue(value);
  if (!parsed) return null;

  const zoned = timeZone ? zonedCalendar(parsed, timeZone) : null;
  const isoDow = zoned ? zoned.isoDow : getIsoDowFromUtcDate(parsed);
  const year = zoned ? zoned.y : parsed.getUTCFullYear();
  const month = zoned ? zoned.mo : parsed.getUTCMonth() + 1;
  const day = zoned ? zoned.d : parsed.getUTCDate();
  const hour = zoned ? zoned.hh : parsed.getUTCHours();
  const minute = zoned ? zoned.mm : parsed.getUTCMinutes();
  const second = zoned ? zoned.ss : parsed.getUTCSeconds();
  return replaceDateFormatTokens(pattern, (token) => {
    switch (token) {
      case 'yyyy':
        return String(year);
      case 'yy':
      case 'YY':
        return formatYearTwoDigits(year);
      case 'MMMM':
      case 'MMM':
        return resolveMonthToken(token, month);
      case 'MM':
        return pad2(month);
      case 'M':
        return String(month);
      case 'dd':
        return pad2(day);
      case 'd':
        return String(day);
      case 'HH':
        return pad2(hour);
      case 'H':
        return String(hour);
      case 'mm':
        return pad2(minute);
      case 'm':
        return String(minute);
      case 'ss':
        return pad2(second);
      case 's':
        return String(second);
      case 'weekday':
      case 'weekdayShort':
      case 'weekdayN':
      case 'w':
      case 'www':
        return resolveWeekdayToken(token, isoDow);
      default:
        return token;
    }
  });
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** Parsea timestamps ISO, Date o cadenas con fecha+hora (naive = wall-clock del dashboard). */
export function parseTemporalValue(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(
      Date.UTC(
        value.getUTCFullYear(),
        value.getUTCMonth(),
        value.getUTCDate(),
        value.getUTCHours(),
        value.getUTCMinutes(),
        value.getUTCSeconds(),
        value.getUTCMilliseconds()
      )
    );
  }
  const s = String(value).trim();
  if (!s) return null;

  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(s);
  const naive = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/);
  if (naive && !hasOffset) {
    return new Date(
      Date.UTC(
        Number(naive[1]),
        Number(naive[2]) - 1,
        Number(naive[3]),
        Number(naive[4] || 0),
        Number(naive[5] || 0),
        Number(naive[6] || 0)
      )
    );
  }

  if (/^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(s)) {
    const normalized = s.includes('T') ? s : s.replace(' ', 'T');
    const d = new Date(normalized);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(`${s}T00:00:00Z`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

function formatUtcParts(date, { withSeconds = false, timeOnly = false, dateOnly = false } = {}) {
  const y = date.getUTCFullYear();
  const mo = pad2(date.getUTCMonth() + 1);
  const d = pad2(date.getUTCDate());
  const hh = pad2(date.getUTCHours());
  const mm = pad2(date.getUTCMinutes());
  const ss = pad2(date.getUTCSeconds());
  if (timeOnly) return withSeconds ? `${hh}:${mm}:${ss}` : `${hh}:${mm}`;
  if (dateOnly) return `${y}-${mo}-${d}`;
  if (withSeconds) return `${y}-${mo}-${d} ${hh}:${mm}:${ss}`;
  return `${y}-${mo}-${d} ${hh}:${mm}`;
}

function formatWithDisplayFormat(date, format, timeZone) {
  if (timeZone) {
    const p = zonedCalendar(date, timeZone);
    const y = String(p.y);
    const mo = pad2(p.mo);
    const d = pad2(p.d);
    const hh = pad2(p.hh);
    const mm = pad2(p.mm);
    const ss = pad2(p.ss);
    switch (format) {
      case 'date':
        return `${y}-${mo}-${d}`;
      case 'datetimeSec':
        return `${y}-${mo}-${d} ${hh}:${mm}:${ss}`;
      case 'time':
        return `${hh}:${mm}`;
      case 'timeSec':
        return `${hh}:${mm}:${ss}`;
      case 'localeDate':
        return date.toLocaleDateString('es-ES', { timeZone });
      case 'localeDatetime':
        return date.toLocaleString('es-ES', {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        });
      default:
        return `${y}-${mo}-${d} ${hh}:${mm}`;
    }
  }
  switch (format) {
    case 'date':
      return formatUtcParts(date, { dateOnly: true });
    case 'datetime':
      return formatUtcParts(date);
    case 'datetimeSec':
      return formatUtcParts(date, { withSeconds: true });
    case 'time':
      return formatUtcParts(date, { timeOnly: true });
    case 'timeSec':
      return formatUtcParts(date, { timeOnly: true, withSeconds: true });
    case 'localeDate':
      return date.toLocaleDateString('es-ES', { timeZone: 'UTC' });
    case 'localeDatetime':
      return date.toLocaleString('es-ES', {
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    default:
      return formatUtcParts(date);
  }
}

/**
 * Formato legible de fecha/hora para tablas y ejes.
 * Prioridad: dateDisplayFormat explícito → agrupación temporal → auto (datetime completo).
 * timeZone formatea instantes. Un dateBucket ya es calendario local (date_trunc en la zona
 * del dashboard) y no se vuelve a desplazar.
 */
export function formatDateTimeDisplay(value, { dateDisplayFormat, dateBucket, timeZone } = {}) {
  if (value === null || value === undefined || value === '') return null;

  const zone = dateBucket && dateBucket !== 'raw' ? undefined : timeZone;
  const fmt = dateDisplayFormat && dateDisplayFormat !== 'auto' ? dateDisplayFormat : null;
  if (fmt) {
    if (isCustomDateDisplayFormat(fmt)) {
      const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(value).trim());
      const custom = formatWithCustomPattern(value, getCustomDateDisplayPattern(fmt), {
        dateBucket,
        timeZone: zone && !dateOnly ? zone : undefined,
      });
      if (custom != null && custom !== '') return custom;
    }
    const parsed = parseTemporalValue(value);
    if (parsed) {
      const s = String(value).trim();
      if (zone && /^\d{4}-\d{2}-\d{2}$/.test(s)) return formatWithDisplayFormat(parsed, fmt);
      return formatWithDisplayFormat(parsed, fmt, zone);
    }
    if (fmt === 'date' || fmt === 'localeDate') {
      const dOnly = formatDateOnlyValue(value);
      if (dOnly) {
        if (fmt === 'localeDate') {
          const d = parseTemporalValue(dOnly);
          if (d) return d.toLocaleDateString('es-ES', { timeZone: 'UTC' });
        }
        return dOnly;
      }
    }
  }

  if (dateBucket && dateBucket !== 'raw') {
    const bucketLabel = formatTemporalBucketValue(value, dateBucket);
    if (bucketLabel) return bucketLabel;
  }

  const parsed = parseTemporalValue(value);
  if (parsed && zone) {
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    return formatWithDisplayFormat(parsed, 'datetime', zone);
  }
  if (parsed) return formatWithDisplayFormat(parsed, 'datetime');

  const dateOnly = formatDateOnlyValue(value);
  if (dateOnly) return dateOnly;

  return null;
}

/** Acorta ISO/datetime a solo fecha (YYYY-MM-DD). */
export function formatDateOnlyValue(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getUTCFullYear();
    const m = String(value.getUTCMonth() + 1).padStart(2, '0');
    const d = String(value.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const isoPrefix = s.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s]|$)/);
  if (isoPrefix) return isoPrefix[1];
  return null;
}

/** Formato legible según agrupación temporal (ejes y celdas de tabla). */
export function formatTemporalBucketValue(value, dateBucket) {
  if (value === null || value === undefined || value === '') return null;

  if (dateBucket === 'dayOfWeek') {
    return formatDayOfWeekLabel(value);
  }

  if (dateBucket === 'hourOfDay') {
    const n = typeof value === 'number' ? value : parseInt(String(value).trim(), 10);
    if (Number.isFinite(n) && n >= 0 && n <= 23) return String(n).padStart(2, '0');
    return null;
  }

  if (dateBucket === 'monthOfYear') {
    return formatMonthOfYearLabel(value);
  }

  if (dateBucket === 'dayOfMonth') {
    return formatDayOfMonthLabel(value);
  }

  if (dateBucket === 'year') {
    const s = String(value).trim();
    if (/^\d{4}$/.test(s)) return s;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return String(value.getUTCFullYear());
    }
    const m = s.match(/^(\d{4})/);
    return m ? m[1] : null;
  }

  if (dateBucket === 'month') {
    const s = String(value).trim();
    if (/^\d{4}-\d{2}$/.test(s)) return s;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const y = value.getUTCFullYear();
      const mo = String(value.getUTCMonth() + 1).padStart(2, '0');
      return `${y}-${mo}`;
    }
    const m = s.match(/^(\d{4}-\d{2})/);
    return m ? m[1] : null;
  }

  if (dateBucket === 'day' || dateBucket === 'week') {
    return formatDateOnlyValue(value);
  }

  if (dateBucket === 'hour' || dateBucket === 'minute') {
    const s = String(value).trim();
    const m = s.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/);
    if (m) return `${m[1]} ${m[2]}`;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const date = formatDateOnlyValue(value);
      const hh = String(value.getUTCHours()).padStart(2, '0');
      const mm = String(value.getUTCMinutes()).padStart(2, '0');
      return dateBucket === 'minute' ? `${date} ${hh}:${mm}` : `${date} ${hh}:00`;
    }
  }

  return null;
}

